import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sticky banner on Admin dashboard that flashes red the moment today's
 * promo + playback spend exceeds the admin-configured daily budget.
 *
 * Polls every 60s so the admin sees the alert within a minute of crossing
 * the threshold.
 */
const CreditBurnBanner = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/recon/burn-rate`);
                if (!cancelled) setData(data);
            } catch { /* network blip — retry next tick */ }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (!data || data.level === 'off' || data.level === 'ok') return null;

    const isOver = data.level === 'over';
    return (
        <div
            className={`mb-3 rounded-xl border-2 px-4 py-3 flex items-center justify-between gap-3 ${
                isOver
                    ? 'border-[#FF3B30]/60 bg-gradient-to-r from-[#FF3B30]/20 via-[#FF6B35]/10 to-[#FF3B30]/10'
                    : 'border-[#FFD700]/40 bg-gradient-to-r from-[#FFD700]/15 to-[#FFD700]/5'
            }`}
            data-testid="credit-burn-banner"
        >
            <div className="flex items-center gap-3">
                {isOver ? (
                    <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
                ) : (
                    <TrendingUp className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                )}
                <div>
                    <p className={`font-black ${isOver ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                        {isOver ? 'DAILY FREE-PLAY BUDGET EXCEEDED' : 'Free-play budget close to limit'}
                    </p>
                    <p className="text-[11px] text-[#A0AAB2]">
                        ${data.spent_today.toFixed(0)} spent on free play today &middot; budget ${data.budget.toFixed(0)} &middot; {data.pct.toFixed(0)}%
                    </p>
                </div>
            </div>
            <a
                href="#reports-tab"
                onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('[data-testid="credit-recon-card"]')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border ${
                    isOver
                        ? 'border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10'
                        : 'border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10'
                }`}
                data-testid="credit-burn-banner-action"
            >
                View →
            </a>
        </div>
    );
};

export default CreditBurnBanner;
